import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ExternalLink, Gift, Clock, Eye, EyeOff, ChevronDown, ChevronUp, CheckCircle, AlertCircle, Loader2, AlertTriangleIcon, UserRound, Pencil, Save, Banknote, BarChart, Settings, ChevronRight, User2, Book } from 'lucide-react';
import { trackPageView, trackButtonClick } from '../firebaseConfig';
import { useAnalytics } from '../lib/AnalyticsContext';
import { Button } from '../components/common/Button';
import { ParticleBackground } from '../components/ParticleBackground';
import { Navbar } from '../components/Navbar/Navbar';
import { Toast } from '../components/common/Toast';
import Lottie from 'lottie-react';
import waitingAnimation from '../assets/mocktrail-waiting.json';
import ambassadorService, { BankDetails, AmbassadorApplication, AmbassadorStatus } from '../services/ambassadorService';
import authService from '../services/authService';
import ssoService, { TokenExchangeResponse } from '../services/ssoService';
import apiClient from '../services/apiClient';
import { SafariPopupGuide } from '../components/common/SafariPopupGuide';
import { isSafariApple } from '../utils/browserDetection';

// Add fadeIn keyframes animation
const fadeInAnimation = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out forwards;
  }
`;

// Interface for ambassador user data
interface AmbassadorUser {
  isLoggedIn: boolean;
  name?: string;
  email?: string;
  referralCode?: string;
  completedSteps: number;
  bankDetails?: BankDetails;
  application?: AmbassadorApplication;
  status?: string;
}

// Interface for FAQ items
interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

// Interface for FAQ item component props
interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onClick: () => void;
  index: number;
  isVisible: boolean;
}

// FAQItem component for smooth animations
const FAQItem: React.FC<FAQItemProps> = ({
  question,
  answer,
  isOpen,
  onClick,
  index,
  isVisible
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen, answer]);

  return (
    <div
      className={`border-b border-border-light dark:border-border-dark transition-all duration-500 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      style={{ transitionDelay: `${index * 0.1 + 0.3}s` }}
    >
      <button
        className="flex items-center justify-between w-full py-5 text-left focus:outline-none"
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark pr-8">
          {question}
        </h3>
        <span className="flex-shrink-0 ml-4">
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-primary-light dark:text-primary-dark" />
          ) : (
            <ChevronDown className="h-5 w-5 text-text-secondary-light dark:text-text-secondary-dark" />
          )}
        </span>
      </button>
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: `${height}px` }}
      >
        <div className="pb-5 text-base text-text-secondary-light dark:text-text-secondary-dark">
          {answer}
        </div>
      </div>
    </div>
  );
};

const AmbassadorProgram: React.FC = () => {
  const navigate = useNavigate();
  const { trackUserAction, trackEvent } = useAnalytics();
  const pageRef = useRef<HTMLDivElement>(null);

  // State for active step (1-indexed)
  const [activeStep, setActiveStep] = useState(1);

  // State to track if all steps are completed and we should show only the final step
  const [showCompletedView, setShowCompletedView] = useState(false);

  // State for user data
  const [userData, setUserData] = useState<AmbassadorUser>({
    isLoggedIn: false,
    completedSteps: 0
  });

  // State for loading states
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [submittingBankDetails, setSubmittingBankDetails] = useState(false);
  const [tokenExchangeLoading, setTokenExchangeLoading] = useState(false);

  // State for bank details form
  const [bankDetails, setBankDetails] = useState<BankDetails>({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: ''
  });

  // State for form validation
  const [formErrors, setFormErrors] = useState<Partial<BankDetails>>({});

  // State for showing/hiding account number
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [editingBankDetails, setEditingBankDetails] = useState(false);

  // State for bank details modal
  const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);
  const [modalBankDetails, setModalBankDetails] = useState<BankDetails>({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: ''
  });
  const [modalFormErrors, setModalFormErrors] = useState<Partial<BankDetails>>({});
  const [modalSubmitting, setModalSubmitting] = useState(false);

  // State for animations and visibility
  const [pageLoaded, setPageLoaded] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const [visibleSteps, setVisibleSteps] = useState<{ [key: number]: boolean }>({
    1: false,
    2: false,
    3: false,
    4: false
  });

  // State for FAQ
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [faqsVisible, setFaqsVisible] = useState(false);
  const faqSectionRef = useRef<HTMLDivElement>(null);

  // Error and success message state
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSafariPopupGuide, setShowSafariPopupGuide] = useState(false);

  // Function to show toast-like messages
  const showMessage = (text: string, type: 'success' | 'error' = 'success') => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // Function to handle SSO authentication
  const handleSSOAuthentication = async (response: TokenExchangeResponse) => {
    try {
      // Extract user from normalized response
      const user = response.user || response.data?.user;

      // Validate that we have user data
      if (!user) {
        console.error("Missing user data in token exchange response:", response);
        throw new Error("Missing user data in authentication response");
      }

      // Create AuthUser object compatible with authService
      const authUser = {
        userId: user._id,
        email: user.email,
        firstName: user.name?.split(' ')[0] || '',
        lastName: user.name?.split(' ').slice(1).join(' ') || '',
        isAmbassador: false, // Will be updated after fetching ambassador status
        accessToken: response.access_token,
        refreshToken: response.refresh_token
      };

      // Store authentication data using authService private methods
      // Note: These are private methods, so we need to use bracket notation
      (authService as any).setAccessToken(response.access_token);
      (authService as any).setRefreshToken(response.refresh_token);
      (authService as any).setUser(authUser);

      // Update user data state
      setUserData(prev => ({
        ...prev,
        isLoggedIn: true,
        name: user.name || user.email?.split('@')[0] || 'User',
        email: user.email
      }));

      console.log('SSO Authentication successful, user:', authUser);
      return authUser;
    } catch (error) {
      console.error('Error handling SSO authentication:', error);
      throw error;
    }
  };

  // Function to fetch user's referral code
  const fetchUserReferralCode = async (): Promise<string | null> => {
    try {
      console.log('Fetching user referral code...');

      // Get the access token for authentication
      const token = authService.getAccessToken();
      if (!token) {
        const noTokenError = new Error('No access token available');
        (noTokenError as any).status = 401;
        throw noTokenError;
      }

      // Make authenticated request to get user's referral code
      const response = await apiClient.get<{ referralCode: string }>('/user-referral/my-code', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('User referral code fetched:', response.referralCode);
      return response.referralCode;
    } catch (error: any) {
      console.error('Error fetching user referral code:', error);

      // Preserve the status code for the calling function
      if (error.status === 401) {
        const authError = new Error('Unauthorized');
        (authError as any).status = 401;
        throw authError;
      }

      // For other errors, return null so the flow continues
      return null;
    }
  };

  // Function to fetch ambassador status from API
  const fetchAmbassadorStatus = async () => {
    try {
      setLoadingData(true);
      console.log('Fetching ambassador status...');

      const status = await ambassadorService.getAmbassadorStatus();
      console.log('Ambassador status fetched:', status);

      if (status.isAmbassador && status.application) {
        const app = status.application;

        // Update user data with real API response
        setUserData(prev => ({
          ...prev,
          isLoggedIn: true,
          application: app,
          status: app.status,
          bankDetails: app.bankDetails,
          completedSteps: ambassadorService.getApplicationCompletionPercentage(app) > 66 ? 3 :
            ambassadorService.isBankDetailsComplete(app.bankDetails) ? 2 : 1
        }));

        // Set bank details if they exist and ensure proper format
        if (app.bankDetails) {
          setBankDetails({
            accountHolderName: app.bankDetails.accountHolderName || '',
            bankName: app.bankDetails.bankName || '',
            accountNumber: app.bankDetails.accountNumber || '',
            ifscCode: app.bankDetails.ifscCode || ''
          });
        }

        // Set active step based on application status
        if (app.status === 'approved') {
          setActiveStep(4);
        } else if (ambassadorService.isBankDetailsComplete(app.bankDetails)) {
          setActiveStep(4); // Move to "Await Approval" if bank details are complete
        } else {
          setActiveStep(3);
        }

        // Fetch user's referral code if they are an ambassador
        try {
          const referralCode = await fetchUserReferralCode();
          if (referralCode) {
            setUserData(prev => ({
              ...prev,
              referralCode: referralCode
            }));
          }
        } catch (referralError) {
          console.log('Could not fetch user referral code:', referralError);
        }
      } else {
        // User is not an ambassador, but may be logged in
        // This will allow the calling function to handle the next step
        throw new Error('User is not an ambassador');
      }

    } catch (error: any) {
      console.error('Error fetching ambassador status:', error);

      // If error is 401 or user not authenticated, preserve the status and throw
      if (error.status === 401) {
        const authError = new Error('Unauthorized');
        (authError as any).status = 401;
        throw authError;
      } else if (error.message === 'User is not an ambassador') {
        // Re-throw this specific error so the calling function can handle it
        throw error;
      } else {
        showMessage('Failed to load ambassador status', 'error');
        throw error; // Re-throw to let the calling function handle it
      }
    } finally {
      setLoadingData(false);
    }
  };

  // Function to apply for ambassador program
  const applyForAmbassador = async () => {
    try {
      setLoading(true);
      console.log('Applying for ambassador program...');

      const application = await ambassadorService.applyForAmbassador();
      console.log('Successfully applied for ambassador program:', application);

      setUserData(prev => ({
        ...prev,
        application,
        status: application.status,
        completedSteps: 1
      }));

      showMessage('Successfully applied for ambassador program!');

      // Try to fetch updated ambassador status and referral code
      setTimeout(async () => {
        try {
          await fetchAmbassadorStatus();
        } catch (error) {
          console.log('Ambassador status not yet updated, proceeding to next step');
        }

        // Move to step 3 (await approval) after successful application
        setActiveStep(3);
      }, 1000);

    } catch (error: any) {
      console.error('Error applying for ambassador program:', error);

      // Handle authentication errors
      if (error.status === 401) {
        console.log('Authentication failed during application, clearing tokens');
        authService.logout();
        setUserData(prev => ({
          ...prev,
          isLoggedIn: false,
          name: undefined,
          email: undefined,
          referralCode: undefined,
          application: undefined,
          bankDetails: undefined,
          status: undefined
        }));
        showMessage('Session expired. Please log in again.', 'error');
      } else {
        showMessage(error.message || 'Failed to apply for ambassador program', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to proceed to next step
  const goToNextStep = () => {
    if (activeStep < 4) {
      setActiveStep(prevStep => prevStep + 1);

      // Update user completed steps if this is a new completion
      if (activeStep >= userData.completedSteps) {
        setUserData(prev => ({
          ...prev,
          completedSteps: activeStep
        }));
      }

      // Scroll to the new active step
      setTimeout(() => {
        const stepElement = document.getElementById(`step-${activeStep + 1}`);
        if (stepElement) {
          stepElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Function to move to a specific step (safer than goToNextStep for button clicks)
  const moveToStep = (stepNumber: number) => {
    if (stepNumber >= 1 && stepNumber <= 4) {
      setActiveStep(stepNumber);

      // Scroll to the target step
      setTimeout(() => {
        const stepElement = document.getElementById(`step-${stepNumber}`);
        if (stepElement) {
          stepElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Function to validate IFSC code
  const validateIFSC = (ifsc: string): boolean => {
    // IFSC code format: 4 alphabets representing bank, followed by 0, followed by 6 alphanumeric for branch
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    return ifscRegex.test(ifsc);
  };

  // Function to validate bank details form
  const validateBankDetails = (): boolean => {
    const errors: Partial<BankDetails> = {};

    if (!bankDetails.accountHolderName.trim()) {
      errors.accountHolderName = 'Account holder name is required';
    }

    if (!bankDetails.bankName.trim()) {
      errors.bankName = 'Bank name is required';
    }

    if (!bankDetails.accountNumber.trim()) {
      errors.accountNumber = 'Account number is required';
    } else if (!/^\d{9,18}$/.test(bankDetails.accountNumber)) {
      errors.accountNumber = 'Account number must be 9-18 digits';
    }

    if (!bankDetails.ifscCode.trim()) {
      errors.ifscCode = 'IFSC code is required';
    } else if (!validateIFSC(bankDetails.ifscCode)) {
      errors.ifscCode = 'Invalid IFSC code format (e.g., HDFC0001234)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle input change for bank details
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBankDetails(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user types
    if (formErrors[name as keyof BankDetails]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Handle form submission for bank details
  const handleSubmitBankDetails = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateBankDetails()) {
      return;
    }

    try {
      setSubmittingBankDetails(true);
      console.log('Submitting bank details...');

      const updatedApplication = await ambassadorService.updateBankDetails(bankDetails);
      console.log('Bank details updated:', updatedApplication);

      // Update user data with the updated application
      setUserData(prev => ({
        ...prev,
        application: updatedApplication,
        bankDetails: updatedApplication.bankDetails,
        completedSteps: 2
      }));

      // Exit edit mode if we were editing
      if (editingBankDetails) {
        setEditingBankDetails(false);
        showMessage('Bank details updated successfully!');
      } else {
        showMessage('Bank details saved successfully!');
        // Only proceed to next step if this is a new submission (not an edit)
        goToNextStep();
      }

      // Track the event
      trackButtonClick(editingBankDetails ? 'Update Bank Details' : 'Submit Bank Details', 'Ambassador Program Page');

    } catch (error: any) {
      console.error('Error updating bank details:', error);

      // Handle authentication errors
      if (error.status === 401) {
        console.log('Authentication failed during bank details update, clearing tokens');
        authService.logout();
        setUserData(prev => ({
          ...prev,
          isLoggedIn: false,
          name: undefined,
          email: undefined,
          referralCode: undefined,
          application: undefined,
          bankDetails: undefined,
          status: undefined
        }));
        showMessage('Session expired. Please log in again.', 'error');
      } else {
        showMessage(error.message || 'Failed to save bank details', 'error');
      }
    } finally {
      setSubmittingBankDetails(false);
    }
  };

  // Get masked account number with better handling of encrypted values
  const getMaskedAccountNumber = (number: string): string => {
    if (!number) return '';

    // If the account number looks encrypted (contains colons), return a generic masked format
    if (number.includes(':')) {
      return '****-****-****-XXXX';
    }

    // For non-encrypted numbers, use the service's masking function
    return ambassadorService.maskAccountNumber(number);
  };

  // Function to handle editing bank details
  const handleEditBankDetails = () => {
    setEditingBankDetails(true);
    // Reset form errors when entering edit mode
    setFormErrors({});
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    setEditingBankDetails(false);
    // Reset bank details to saved values
    if (userData.bankDetails) {
      setBankDetails({
        accountHolderName: userData.bankDetails.accountHolderName || '',
        bankName: userData.bankDetails.bankName || '',
        accountNumber: userData.bankDetails.accountNumber || '',
        ifscCode: userData.bankDetails.ifscCode || ''
      });
    }
    setFormErrors({});
  };

  // Function to simulate login and fetch user data from backend
  const fetchUserData = async () => {
    setLoadingData(true);

    try {
      // Check if user is authenticated using authService
      if (!authService.isAuthenticated()) {
        console.log('User not authenticated, showing login flow');
        setUserData(prev => ({
          ...prev,
          isLoggedIn: false
        }));
        return;
      }

      // Get current user data
      const currentUser = authService.getCurrentUser();
      console.log('User appears authenticated from localStorage:', currentUser);

      // Verify authentication by making a test API call
      // Instead of fetching referral code first, let's try ambassador status to validate auth
      try {
        await fetchAmbassadorStatus();
        // If we get here, the user is properly authenticated and is an ambassador
        console.log('User is authenticated and is an ambassador');

        // Set user as logged in
        setUserData(prev => ({
          ...prev,
          isLoggedIn: true,
          name: currentUser?.name || currentUser?.email?.split('@')[0] || 'User',
          email: currentUser?.email
        }));

        // Try to fetch referral code
        try {
          const referralCode = await fetchUserReferralCode();
          if (referralCode) {
            setUserData(prev => ({
              ...prev,
              referralCode: referralCode
            }));
          }
        } catch (referralError) {
          console.log('Could not fetch user referral code:', referralError);
        }

      } catch (ambassadorError: any) {
        console.log('Ambassador status fetch failed:', ambassadorError);

        // If we get a 401, the tokens are invalid - clear them
        if (ambassadorError.status === 401) {
          console.log('Tokens are invalid (401), clearing authentication');
          authService.logout();
          setUserData(prev => ({
            ...prev,
            isLoggedIn: false,
            name: undefined,
            email: undefined,
            referralCode: undefined,
            application: undefined,
            bankDetails: undefined,
            status: undefined
          }));
          return;
        }

        // If it's not a 401, the user is authenticated but not an ambassador
        console.log('User is authenticated but not an ambassador');

        // Set user as logged in
        setUserData(prev => ({
          ...prev,
          isLoggedIn: true,
          name: currentUser?.name || currentUser?.email?.split('@')[0] || 'User',
          email: currentUser?.email
        }));

        // Try to fetch referral code for authenticated users
        try {
          const referralCode = await fetchUserReferralCode();
          if (referralCode) {
            setUserData(prev => ({
              ...prev,
              referralCode: referralCode
            }));
          }
        } catch (referralError: any) {
          // If referral code fetch also fails with 401, clear auth
          if (referralError.status === 401) {
            console.log('Referral code fetch failed with 401, clearing authentication');
            authService.logout();
            setUserData(prev => ({
              ...prev,
              isLoggedIn: false,
              name: undefined,
              email: undefined,
              referralCode: undefined
            }));
            return;
          }
          console.log('Could not fetch user referral code:', referralError);
        }

        // For existing users who are not ambassadors, set them to step 2
        if (activeStep === 1) {
          setActiveStep(2);
        }
      }

    } catch (error: any) {
      console.error('Error in fetchUserData:', error);

      // Handle authentication errors
      if (error.status === 401) {
        console.log('Authentication failed, clearing tokens');
        authService.logout();
        setUserData(prev => ({
          ...prev,
          isLoggedIn: false,
          name: undefined,
          email: undefined,
          referralCode: undefined,
          application: undefined,
          bankDetails: undefined,
          status: undefined
        }));
      }
    } finally {
      setLoadingData(false);
    }
  };

  // Set up step visibility observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        const stepId = entry.target.id;
        if (stepId && stepId.startsWith('step-')) {
          const stepNumber = parseInt(stepId.split('-')[1]);
          if (entry.isIntersecting) {
            setVisibleSteps(prev => ({ ...prev, [stepNumber]: true }));
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all step elements
    for (let i = 1; i <= 4; i++) {
      const stepElement = document.getElementById(`step-${i}`);
      if (stepElement) {
        observer.observe(stepElement);
      }
    }

    // Observe FAQ section
    if (faqSectionRef.current) {
      const faqObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setFaqsVisible(true);
            faqObserver.disconnect();
          }
        },
        { threshold: 0.1 }
      );

      faqObserver.observe(faqSectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [pageLoaded]);

  // Sequence page animations
  useEffect(() => {
    // Set page as loaded after a short delay to trigger animations
    const headerTimer = setTimeout(() => {
      setPageLoaded(true);
    }, 2000);

    // Show content after header appears
    const contentTimer = setTimeout(() => {
      setContentVisible(true);
      setVisibleSteps(prev => ({ ...prev, 1: true })); // Make first step visible immediately
    }, 2300);

    return () => {
      clearTimeout(headerTimer);
      clearTimeout(contentTimer);
    };
  }, []);

  // Track page view and check authentication when the page loads
  useEffect(() => {
    // Set document title
    document.title = 'MockTrail Ambassador Program - Earn While Helping Others';
    
    trackPageView('Ambassador Program Page');

    // Force scroll to top on page load
    window.scrollTo(0, 0);

    // Check if user is authenticated and fetch their ambassador status
    fetchUserData();
  }, []);

  // Update completed view state when user data changes
  useEffect(() => {
    const isCompleted = !!(userData.application &&
      userData.status === 'approved' &&
      userData.bankDetails &&
      ambassadorService.isBankDetailsComplete(userData.bankDetails) &&
      userData.referralCode);

    setShowCompletedView(isCompleted);

    // If completed, ensure we're on step 4
    if (isCompleted && activeStep !== 4) {
      setActiveStep(4);
    }
  }, [userData, activeStep]);

  // Handle modal keyboard events and body scroll
  useEffect(() => {
    if (showBankDetailsModal) {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';

      // Handle ESC key to close modal
      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          handleCloseBankDetailsModal();
        }
      };

      document.addEventListener('keydown', handleEscapeKey);

      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [showBankDetailsModal]);

  const handleTryMockTrail = async () => {
    setLoading(true);
    setErrorMessage(null);
    setShowSafariPopupGuide(false);

    // Track button click
    trackButtonClick('Try MockTrail', 'Ambassador Program Page');

    trackUserAction('conversion_action', {
      action: 'try_mocktrail',
      location: 'ambassador_program_page'
    });

    try {
      // Get stored referral code if any
      const storedReferralCode = localStorage.getItem('referralCode');
      console.log("Using stored referral code:", storedReferralCode);

      // First, initiate the JobProt SSO process to get the authorization URL
      const authResponse = await ssoService.initiateJobProtSSO();
      const { url, state } = authResponse;

      if (!url || !state) {
        throw new Error('Invalid authentication response: Missing URL or state');
      }

      console.log("Opening SSO popup with URL:", url);

      // Open popup with the URL from the response
      const popupResponse = await ssoService.openSSOPopup(url);

      console.log("Received popup response:", JSON.stringify(popupResponse));

      // Make sure we have a code from the popup
      if (!popupResponse.params.code) {
        throw new Error('Authentication failed: No authorization code received');
      }

      // Set loading state for token exchange
      setTokenExchangeLoading(true);

      // Exchange code for token
      const response = await ssoService.exchangeToken({
        code: popupResponse.params.code,
        state: state,
        referralCode: storedReferralCode || undefined,
        isAmbassador: true // Always set to true since this is the ambassador program page
      });

      console.log("Token exchange successful:", response);

      // Handle SSO authentication
      const authUser = await handleSSOAuthentication(response);

      // If successful, clear the stored referral code as it's been used
      if (storedReferralCode) {
        localStorage.removeItem('referralCode');
      }

      showMessage('Successfully logged in!');

      // Check if this is a new user who signed up from ambassador page
      const user = response.user || response.data?.user;
      if (user?.newUser) {
        console.log('New user detected, they should already have ambassador application created');
        // For new users, the backend should have already created an ambassador application
        // Try to fetch ambassador status after a short delay
        setTimeout(async () => {
          try {
            await fetchAmbassadorStatus();
            showMessage('Welcome! Your ambassador application has been submitted.');
          } catch (error) {
            console.log('New user ambassador application might still be processing');
          }
        }, 2000);
      } else {
        console.log('Existing user detected, they need to apply for ambassador program');
        // For existing users, they need to apply manually
      }

      // Try to fetch ambassador status after successful login
      setTimeout(async () => {
        try {
          await fetchAmbassadorStatus();
        } catch (error) {
          console.log('User is logged in but not yet an ambassador, showing step flow');
          // User is authenticated but not an ambassador, proceed to step 2
          setActiveStep(2);

          // Try to fetch user's referral code
          try {
            const referralCode = await fetchUserReferralCode();
            if (referralCode) {
              setUserData(prev => ({
                ...prev,
                referralCode: referralCode
              }));
            }
          } catch (referralError) {
            console.log('Could not fetch user referral code:', referralError);
          }
        }
      }, 1000);

    } catch (error: any) {
      console.error('JobProt SSO failed:', error);

      // Handle popup blocked error specifically for Safari on Apple devices
      if (error?.message?.includes('Popup was blocked')) {
        if (isSafariApple()) {
          // Show Safari-specific popup guide instead of generic error
          setShowSafariPopupGuide(true);
          return; // Don't set generic error message
        } else {
          setErrorMessage('Please allow popups for this site to login with JobProt.');
        }
      } else if (error?.message?.includes('Authentication cancelled')) {
        setErrorMessage('Login was cancelled. Please try again.');
      } else if (error?.message?.includes('Invalid authentication response')) {
        setErrorMessage('Could not initialize authentication. Please try again later.');
      } else if (error?.message?.includes('No authorization code')) {
        setErrorMessage('Authentication failed. No authorization code received.');
      } else {
        setErrorMessage('Failed to authenticate with JobProt. Please try again.');
      }

      // Show toast message if available (but not for Safari popup guide)
      if (!showSafariPopupGuide) {
        showMessage('Failed to authenticate with JobProt. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
      setTokenExchangeLoading(false);
    }
  };

  // Retry function for Safari popup guide
  const handleRetrySSO = () => {
    setShowSafariPopupGuide(false);
    handleTryMockTrail();
  };

  const handleSignUp = () => {
    // Track signup attempt
    trackButtonClick('Create an account', 'Ambassador Program Page');

    trackUserAction('signup_initiated', {
      method: 'jobprot',
      page: 'ambassador_program',
      referrer: document.referrer || 'direct'
    });

    // Open signup page with ambassador flag
    const signupUrl = new URL('https://dashboard.jobprot.com/sign-up');
    signupUrl.searchParams.set('isAmbassador', 'true');

    window.open(signupUrl.toString(), '_blank');
  };

  // Toggle FAQ item
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // FAQs data
  const faqs: FAQItem[] = [
    {
      question: "How much can I earn as an ambassador?",
      answer: (
        <p>Your earning potential is unlimited! You <span className="text-text-primary-light dark:text-text-primary-dark">earn a {import.meta.env.VITE_AMBASSADOR_PROGRAM_REFERRAL_PERCENTAGE || 10}% commission</span> on every subscription purchased using your referral code. For example, if someone signs up using your code & purchases a plan worth ₹1500, you'll earn ₹{Math.round(1500 * (parseInt(import.meta.env.VITE_AMBASSADOR_PROGRAM_REFERRAL_PERCENTAGE as string) || 10) / 100)}. The more people you refer, the more you earn!</p>
      )
    },
    {
      question: "When and how will I receive my payments?",
      answer: (
        <p>Payments are processed automatically every 15 days directly to your registered bank account via <span className="text-text-primary-light dark:text-text-primary-dark">Bank Transfer</span>. The minimum payout threshold <span className="text-text-primary-light dark:text-text-primary-dark">is ₹{import.meta.env.VITE_AMBASSADOR_MINIMUM_PAYOUT || 1000}</span>. You must have a valid bank account in India to receive payments.</p>
      )
    },
    {
      question: "Who can become a MockTrail Ambassador?",
      answer: (
        <p><span className="text-text-primary-light dark:text-text-primary-dark">Anyone</span> can join our ambassador program! We welcome students, professionals, career coaches, content creators, and anyone passionate about helping others succeed in their interviews. You must be at least 18 years old and have a valid bank account in India to receive payments.</p>
      )
    },
    {
      question: "How do I track my referrals and earnings?",
      answer: (
        <p>Once approved, you can access your dashboard <a href="https://app.mocktrail.app" target="_blank" rel="noopener noreferrer" className="text-text-primary-light dark:text-text-primary-dark">here</a>. Login and go to the settings to find your referral statistics, and the amount you have earned.</p>
      )
    },
    {
      question: "Will I receive any material to promote MockTrail?",
      answer: (
        <p>As an ambassador, you'll get exculsive <span className="text-text-primary-light dark:text-text-primary-dark">MockTrail merch and coupon codes</span> to share with your friends. The merch will be delivered to you after you have <span className="text-text-primary-light dark:text-text-primary-dark">completed atleast 10 referrals</span>.</p>
      )
    }
  ];

  // Custom background gradient styles
  const backgroundGradient = {
    background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
    position: 'relative' as 'relative',
    zIndex: 0,
    overflow: 'hidden' as 'hidden'
  };

  // Step state styles
  const getStepStyles = (stepNumber: number) => {
    if (stepNumber === activeStep) {
      return "opacity-100"; // Active step
    } else if (stepNumber < activeStep) {
      return "opacity-80 hover:opacity-100"; // Completed step
    } else {
      return "opacity-40 pointer-events-none"; // Disabled step
    }
  };

  // Calculate line progress based on active step
  const getLineProgress = (stepNumber: number) => {
    if (stepNumber >= activeStep) {
      return '0%'; // Line not drawn yet
    }
    return '100%'; // Line fully drawn
  };

  // Functions for bank details modal
  const handleOpenBankDetailsModal = () => {
    // Pre-fill modal with current bank details
    if (userData.bankDetails) {
      setModalBankDetails({
        accountHolderName: userData.bankDetails.accountHolderName || '',
        bankName: userData.bankDetails.bankName || '',
        accountNumber: userData.bankDetails.accountNumber || '',
        ifscCode: userData.bankDetails.ifscCode || ''
      });
    }
    setModalFormErrors({});
    setShowBankDetailsModal(true);
  };

  const handleCloseBankDetailsModal = () => {
    setShowBankDetailsModal(false);
    setModalFormErrors({});
    setModalSubmitting(false);
  };

  const handleModalInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setModalBankDetails(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user types
    if (modalFormErrors[name as keyof BankDetails]) {
      setModalFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateModalBankDetails = (): boolean => {
    const errors: Partial<BankDetails> = {};

    if (!modalBankDetails.accountHolderName.trim()) {
      errors.accountHolderName = 'Account holder name is required';
    }

    if (!modalBankDetails.bankName.trim()) {
      errors.bankName = 'Bank name is required';
    }

    if (!modalBankDetails.accountNumber.trim()) {
      errors.accountNumber = 'Account number is required';
    } else if (!/^\d{9,18}$/.test(modalBankDetails.accountNumber)) {
      errors.accountNumber = 'Account number must be 9-18 digits';
    }

    if (!modalBankDetails.ifscCode.trim()) {
      errors.ifscCode = 'IFSC code is required';
    } else if (!validateIFSC(modalBankDetails.ifscCode)) {
      errors.ifscCode = 'Invalid IFSC code format (e.g., HDFC0001234)';
    }

    setModalFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleModalSubmitBankDetails = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateModalBankDetails()) {
      return;
    }

    try {
      setModalSubmitting(true);
      console.log('Updating bank details via modal...');

      const updatedApplication = await ambassadorService.updateBankDetails(modalBankDetails);
      console.log('Bank details updated via modal:', updatedApplication);

      // Update user data with the updated application
      setUserData(prev => ({
        ...prev,
        application: updatedApplication,
        bankDetails: updatedApplication.bankDetails
      }));

      showMessage('Bank details updated successfully!');
      handleCloseBankDetailsModal();

      // Track the event
      trackButtonClick('Update Bank Details Modal', 'Ambassador Program Page');

    } catch (error: any) {
      console.error('Error updating bank details via modal:', error);

      // Handle authentication errors
      if (error.status === 401) {
        console.log('Authentication failed during bank details update, clearing tokens');
        authService.logout();
        setUserData(prev => ({
          ...prev,
          isLoggedIn: false,
          name: undefined,
          email: undefined,
          referralCode: undefined,
          application: undefined,
          bankDetails: undefined,
          status: undefined
        }));
        showMessage('Session expired. Please log in again.', 'error');
        handleCloseBankDetailsModal();
      } else {
        showMessage(error.message || 'Failed to update bank details', 'error');
      }
    } finally {
      setModalSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-background-light dark:bg-background-dark" style={backgroundGradient} ref={pageRef}>
      {/* Add the style tag with keyframes animation */}
      <style dangerouslySetInnerHTML={{ __html: fadeInAnimation }} />
      <ParticleBackground />
      <Navbar />
      <div className="pt-32 pb-24 px-4 md:px-8 lg:px-12">
        {/* Header Section */}
        <div
          className={`max-w-6xl mx-auto text-center mb-16 mt-16 transform transition-all duration-700 ${pageLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
        >
          <h1 className="text-5xl md:text-5xl font-heading font-bold mb-6 bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
            MockTrail Ambassador Program
          </h1>
          <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto leading-relaxed">
            Join our exclusive ambassador program and <span className="text-text-primary-light dark:text-text-primary-dark">earn real money</span> while helping others ace their interviews.
            Share your referral code and get <span className="text-text-primary-light dark:text-text-primary-dark">{import.meta.env.VITE_AMBASSADOR_PROGRAM_REFERRAL_PERCENTAGE || 10}% of their subscription</span> for every user.
          </p>
        </div>

        {/* Steps Section */}
        <div className={`max-w-5xl mx-auto transition-opacity duration-500 ${contentVisible ? 'opacity-100' : 'opacity-0'}`}>
          {showCompletedView ? (
            /* Completed View - Only Show Share & Earn Rewards */
            <div
              id="completed-ambassador"
              className={`relative transition-all duration-500 transform ${visibleSteps[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
            >
              {/* Step Content */}
              <div className="pb-8 md:pb-16 relative">
                <h2 className="text-xl font-heading font-bold mb-4 text-text-primary-light dark:text-text-primary-dark flex items-center gap-3">
                  Share & Earn Rewards
                </h2>
                <div className="bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark rounded-2xl p-4 md:p-8  transition-all duration-300 hover:">
                  <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-6 text-center">
                    Share your code with others and earn rewards when they sign up using your referral code:
                  </p>

                  {userData.referralCode && (
                    <div className="bg-background-light/50 dark:bg-background-dark/50 border border-border-light dark:border-border-dark rounded-lg p-4 mb-6 text-center">
                      <h3 className="font-medium text-lg mb-2 text-text-primary-light dark:text-text-primary-dark">Your referral link:</h3>
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <code className="text-sm block p-3 rounded-md bg-primary-light/10 dark:bg-primary-dark/10 font-mono text-primary-light dark:text-primary-dark text-lg max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                          {import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL}/signup?ref={userData.referralCode}
                        </code>
                        <button
                          className="flex-shrink-0 p-2 rounded-full bg-surface-light dark:bg-surface-dark hover:bg-surface-light/80 dark:hover:bg-surface-dark/80 transition-colors"
                          onClick={() => {
                            navigator.clipboard.writeText(`${import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL}/signup?ref=${userData.referralCode}`);
                            showMessage('Referral code copied to clipboard!');
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary-light dark:text-text-primary-dark"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-6 max-w-xl mx-auto text-left">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-light/20 dark:bg-primary-dark/20 flex items-center justify-center text-primary-light dark:text-primary-dark">
                        <Gift size={20} />
                      </div>
                      <div>
                        <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark">Cash Rewards</h4>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          Earn {import.meta.env.VITE_AMBASSADOR_PROGRAM_REFERRAL_PERCENTAGE || 10}% commission on every subscription purchased with your code
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-light/20 dark:bg-primary-dark/20 flex items-center justify-center text-primary-light dark:text-primary-dark">
                        <Clock size={20} />
                      </div>
                      <div>
                        <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark">Free Interview Minutes</h4>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          Earn additional interview minutes for each successful referral
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-light/20 dark:bg-primary-dark/20 flex items-center justify-center text-primary-light dark:text-primary-dark">
                        <Sparkles size={20} />
                      </div>
                      <div>
                        <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark">Exclusive Access</h4>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          Get early access to new features and premium content
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 text-center">
                    <div className="p-4 bg-primary-light/10 dark:bg-primary-dark/10 rounded-lg inline-block">
                      <p className="text-primary-light dark:text-primary-dark">
                        🎉 You're all set! Start sharing your referral code and earn rewards!
                      </p>
                    </div>
                  </div>

                  {/* Bank Details Edit Option */}
                  <div className="mt-6 pt-6 border-t border-border-light/30 dark:border-border-dark/30">
                    <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                      <div className="text-center md:text-left">
                        <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                          Need to change your bank account details?
                        </h4>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          Update your bank information for receiving payments
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleOpenBankDetailsModal}
                        leftIcon={<Pencil size={16} />}
                      >
                        Edit Bank Details
                      </Button>
                    </div>
                  </div>

                  {/* Stats and Payments Dashboard */}
                  <div className="mt-6 pt-6 border-t border-border-light/30 dark:border-border-dark/30">
                    <div className="bg-gradient-to-r from-primary-light/10 to-secondary-light/10 dark:from-primary-dark/10 dark:to-secondary-dark/10 rounded-xl p-6 border border-primary-light/20 dark:border-primary-dark/20">
                      <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                        <div className="text-center md:text-left">
                          <h4 className="font-medium text-lg text-text-primary-light dark:text-text-primary-dark mb-2 flex items-center gap-2">
                            Track Your Ambassador Performance
                          </h4>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-3">
                            Monitor your referrals, earnings, and payment history in your dashboard
                          </p>
                        </div>
                        <Button
                          variant="primary"
                          size="md"
                          onClick={() => {
                            const dashboardUrl = `${import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL}/settings-preferences?utm_source=ambassador_program&utm_medium=landing_page&utm_campaign=ambassador_dashboard&utm_content=stats_button`;
                            window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
                          }}
                          leftIcon={<BarChart size={18} />}
                          className="shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                        >
                          View Dashboard
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Regular Steps View */
            <>
              {/* Step 1 */}
              <div
                id="step-1"
                className={`relative transition-all duration-500 mb-4 transform ${getStepStyles(1)} ${visibleSteps[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                style={{ transitionDelay: '0.1s' }}
              >
                {/* Step Indicator - Desktop */}
                <div className={`hidden md:flex items-center justify-center absolute -left-6 sm:-left-12 top-0 w-12 h-12 rounded-full ${activeStep >= 1 ? 'bg-primary-light dark:bg-primary-dark' : 'bg-gray-400 dark:bg-gray-600'} text-white font-bold text-xl z-10 transition-all duration-300`}>
                  1
                </div>

                {/* Vertical Dotted Line - Desktop only */}
                <div className="hidden md:block absolute left-0 sm:-left-6 top-12 bottom-0 w-0.5 z-0">
                  <div className="h-full w-full border-l-2 border-dashed border-primary-light/50 dark:border-primary-dark/50"></div>
                  <div
                    className="absolute top-0 left-0 w-full bg-primary-light dark:bg-primary-dark transition-all duration-1000 ease-out"
                    style={{
                      height: getLineProgress(1),
                      opacity: visibleSteps[1] ? 1 : 0
                    }}
                  ></div>
                </div>

                {/* Step Content */}
                <div className="md:ml-8 md:sm:ml-12 pb-8 md:pb-16 relative">
                  <h2 className="text-2xl font-heading font-bold mb-4 text-text-primary-light dark:text-text-primary-dark flex items-center gap-3">
                    {/* Mobile Step Number Badge */}
                    <span className={`md:hidden flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${activeStep >= 1 ? 'bg-primary-light dark:bg-primary-dark text-white' : 'bg-gray-400 dark:bg-gray-600 text-white'}`}>
                      1
                    </span>
                    Create an Account
                  </h2>
                  <div className="flex flex-col text-center items-center justify-center bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark rounded-2xl p-6 md:p-8  transition-all duration-300">
                    {userData.isLoggedIn ? (
                      <div className="space-y-4">
                        <div className="p-4 rounded-lg bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark">
                          <p className="text-success-light dark:text-success-dark text-base">
                            ✅ Account Linked Successfully
                          </p>
                        </div>
                        <div className="p-4 bg-background-light/50 dark:bg-background-dark/50 rounded-lg">
                          <h3 className="text-lg font-medium mb-2 text-text-primary-light dark:text-text-primary-dark">
                            Welcome, {userData.name}!
                          </h3>
                          <p className="text-base text-text-secondary-light dark:text-text-secondary-dark">
                            {userData.email}
                          </p>
                        </div>
                        {userData.application && (
                          <div className="p-4 bg-primary-light/10 dark:bg-primary-dark/10 rounded-lg border border-primary-light/30 dark:border-primary-dark/30">
                            <p className="text-primary-light dark:text-primary-dark text-sm">
                              🎉 You're already in the Ambassador Program! Status: <span className="font-medium capitalize">{userData.status}</span>
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Error Message - Display if error exists */}
                        {errorMessage && (
                          <div className="w-full flex flex-row max-w-sm mb-4 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-lg p-3 border border-red-200 dark:border-red-800/50 text-sm justify-center items-center">
                            <AlertTriangleIcon className="text-red-500 dark:text-red-400 mr-2" size={20} />
                            {errorMessage}
                          </div>
                        )}

                        {/* Safari Popup Guide - Show instead of error for Safari popup blocking */}
                        {showSafariPopupGuide && (
                          <div className="text-start mb-4">
                            <SafariPopupGuide onRetry={handleRetrySSO} />
                          </div>
                        )}

                        <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-6">
                          To become a MockTrail Ambassador, you first need to create an account.
                          If you already have a JobProt account, you can use it to log in to MockTrail.
                        </p>

                        {/* Buttons similar to LoginForm.tsx */}
                        <div className="space-y-6 max-w-sm mx-auto">
                          <Button
                            variant="primary"
                            fullWidth
                            size="lg"
                            loading={loading || tokenExchangeLoading}
                            onClick={handleTryMockTrail}
                            className="mb-2"
                            leftIcon={<UserRound size={18} className="transition-transform duration-300 group-hover:scale-110" />}
                          >
                            <span className="text-lg">
                              {tokenExchangeLoading ? 'Completing Sign-in...' : loading ? 'Opening JobProt...' : 'Login with JobProt'}
                            </span>
                          </Button>

                          <p className="text-sm text-center text-text-secondary-light dark:text-text-secondary-dark">
                            Don't have an account? Sign up using <span className="text-text-primary-light dark:text-text-primary-dark">JobProt</span>
                          </p>

                          <Button
                            variant="secondary"
                            fullWidth
                            size="md"
                            className="py-4"
                            onClick={handleSignUp}
                            rightIcon={<ExternalLink size={18} className="transition-transform duration-300 group-hover:scale-110" />}
                          >
                            Create an account
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Step 2 - Apply for Ambassador Program */}
              <div
                id="step-2"
                className={`relative transition-all duration-500 mb-4 transform ${getStepStyles(2)} ${visibleSteps[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                style={{ transitionDelay: '0.2s' }}
              >
                {/* Step Indicator - Desktop */}
                <div className={`hidden md:flex items-center justify-center absolute -left-6 sm:-left-12 top-0 w-12 h-12 rounded-full ${activeStep >= 2 ? 'bg-primary-light dark:bg-primary-dark' : 'bg-gray-400 dark:bg-gray-600'} text-white font-bold text-xl z-10 transition-all duration-300`}>
                  2
                </div>

                {/* Vertical Dotted Line - Desktop only */}
                <div className="hidden md:block absolute left-0 sm:-left-6 top-12 bottom-0 w-0.5 z-0">
                  <div className="h-full w-full border-l-2 border-dashed border-primary-light/50 dark:border-primary-dark/50"></div>
                  <div
                    className="absolute top-0 left-0 w-full bg-primary-light dark:bg-primary-dark transition-all duration-1000 ease-out"
                    style={{
                      height: getLineProgress(2),
                      opacity: visibleSteps[2] ? 1 : 0
                    }}
                  ></div>
                </div>

                {/* Step Content */}
                <div className="md:ml-8 md:sm:ml-12 pb-8 md:pb-16 relative">
                  <h2 className="text-2xl font-heading font-bold mb-4 text-text-primary-light dark:text-text-primary-dark flex items-center gap-3">
                    {/* Mobile Step Number Badge */}
                    <span className={`md:hidden flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${activeStep >= 2 ? 'bg-primary-light dark:bg-primary-dark text-white' : 'bg-gray-400 dark:bg-gray-600 text-white'}`}>
                      2
                    </span>
                    Apply Now
                  </h2>
                  <div className="text-center bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark rounded-2xl p-6 md:p-8 transition-all duration-300 hover:">
                    {userData.application ? (
                      <div className="space-y-4">
                        <div className="p-4 rounded-lg bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark">
                          <p className="text-success-light dark:text-success-dark text-base">
                            ✅ Application Submitted Successfully
                          </p>
                        </div>
                        {/* <div className="p-4 bg-primary-light/10 dark:bg-primary-dark/10 rounded-lg border border-primary-light/30 dark:border-primary-dark/30">
                          <p className="text-primary-light dark:text-primary-dark text-sm">
                            Please complete the next step to await approval.
                          </p>
                        </div> */}
                        <div className="flex text-center justify-center">
                          <Button
                            variant="secondary"
                            onClick={() => moveToStep(3)}
                            className="mt-6"
                          >
                            Continue to Next Step
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-6">
                          Submit your application to join the MockTrail Ambassador Program.
                          Once approved, you'll be able to earn commissions by referring new users.
                        </p>

                        <div className="space-y-6 max-w-sm mx-auto">
                          <Button
                            variant="primary"
                            fullWidth
                            size="lg"
                            loading={loading}
                            onClick={applyForAmbassador}
                            className="mb-2"
                            leftIcon={<Sparkles size={18} className="transition-transform duration-300 group-hover:scale-110" />}
                          >
                            <span className="text-lg">
                              {loading ? 'Submitting Application...' : 'Apply Now'}
                            </span>
                          </Button>

                          <div className="p-4 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg">
                            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                              <strong>What you'll get:</strong> <span className="text-text-primary-light dark:text-text-primary-dark">Earn {import.meta.env.VITE_AMBASSADOR_PROGRAM_REFERRAL_PERCENTAGE || 10}%</span> commission on every subscription, exclusive merch, and more.
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Step 3 - WAITING FOR APPROVAL (previously step 4) */}
              <div
                id="step-3"
                className={`relative transition-all duration-500 mb-4 transform ${getStepStyles(3)} ${visibleSteps[3] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                style={{ transitionDelay: '0.3s' }}
              >
                {/* Step Indicator - Desktop */}
                <div className={`hidden md:flex items-center justify-center absolute -left-6 sm:-left-12 top-0 w-12 h-12 rounded-full ${activeStep >= 3 ? 'bg-primary-light dark:bg-primary-dark' : 'bg-gray-400 dark:bg-gray-600'} text-white font-bold text-xl  z-10 transition-all duration-300`}>
                  3
                </div>

                {/* Vertical Dotted Line - Desktop only */}
                <div className="hidden md:block absolute left-0 sm:-left-6 top-12 bottom-0 w-0.5 z-0">
                  <div className="h-full w-full border-l-2 border-dashed border-primary-light/50 dark:border-primary-dark/50"></div>
                  <div
                    className="absolute top-0 left-0 w-full bg-primary-light dark:bg-primary-dark transition-all duration-1000 ease-out"
                    style={{
                      height: getLineProgress(3),
                      opacity: visibleSteps[3] ? 1 : 0
                    }}
                  ></div>
                </div>

                {/* Step Content */}
                <div className="md:ml-8 md:sm:ml-12 pb-8 md:pb-16 relative">
                  <h2 className="text-2xl font-heading font-bold mb-4 text-text-primary-light dark:text-text-primary-dark flex items-center gap-3">
                    {/* Mobile Step Number Badge */}
                    <span className={`md:hidden flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${activeStep >= 3 ? 'bg-primary-light dark:bg-primary-dark text-white' : 'bg-gray-400 dark:bg-gray-600 text-white'}`}>
                      3
                    </span>
                    Await Approval
                  </h2>
                  <div className="bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark rounded-2xl p-6  transition-all duration-300 hover: text-center">
                    {activeStep >= 4 ? (
                      <div className="bg-primary-light/10 dark:bg-primary-dark/10 border border-primary-light/30 dark:border-primary-dark/30 rounded-lg p-3 mb-6">
                        <p className="text-primary-light dark:text-primary-dark font-medium">
                          ✅ Your application has been approved!
                        </p>
                      </div>
                    ) : userData.application ? (
                      <>
                        <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-6">
                          Great! You've submitted your application. Our <span className="text-text-primary-light dark:text-text-primary-dark font-medium">team will reach out to you</span> for further details & review your application.
                        </p>

                        <div className="flex justify-center my-6">
                          <div className="p-3 rounded-xl bg-primary-light/10 dark:bg-primary-dark/10 border border-primary-light/30 dark:border-primary-dark/30 inline-flex items-center space-x-2">
                            <Clock className="w-6 h-6 text-primary-light dark:text-primary-dark animate-pulse" />
                            <span className="text-base text-text-primary-light dark:text-text-primary-dark font-medium">Under Review</span>
                          </div>
                        </div>

                        <div className="flex justify-center mb-6">
                          <div className="w-full max-w-[140px] h-[140px] -m-10">
                            <Lottie
                              animationData={waitingAnimation}
                              loop={true}
                              autoplay={true}
                              className="w-full h-full scale-140"
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-6">
                          Once you've submitted your application, our <span className="text-text-primary-light dark:text-text-primary-dark font-medium">team will review it</span> and reach out to you for further details.
                        </p>

                        <div className="flex justify-center my-6">
                          <div className="p-4 rounded-xl bg-background-light/50 dark:bg-background-dark/50 border border-border-light dark:border-border-dark inline-flex items-center space-x-2">
                            <Clock className="w-6 h-6 text-primary-light dark:text-primary-dark animate-pulse" />
                            <span className="text-base text-text-primary-light dark:text-text-primary-dark">Submit Your Application First!</span>
                          </div>
                        </div>
                      </>
                    )}

                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                      You'll receive <span className="text-text-primary-light dark:text-text-primary-dark font-medium">an email notification</span> once your application is approved. Then you can start sharing your referral code and earning rewards!
                    </p>

                    {activeStep >= 4 && (
                      <div className="flex justify-center">
                        <Button
                          variant="secondary"
                          onClick={() => moveToStep(4)}
                          className="mt-6"
                        >
                          Continue to Final Step
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Step 4 - Share & Earn Rewards (previously step 5) */}
              <div
                id="step-4"
                className={`relative transition-all duration-500 transform ${getStepStyles(4)} ${visibleSteps[4] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                style={{ transitionDelay: '0.4s' }}
              >
                {/* Step Indicator - Desktop */}
                <div className={`hidden md:flex items-center justify-center absolute -left-6 sm:-left-12 top-0 w-12 h-12 rounded-full ${activeStep >= 4 ? 'bg-primary-light dark:bg-primary-dark' : 'bg-gray-400 dark:bg-gray-600'} text-white font-bold text-xl  z-10 transition-all duration-300`}>
                  4
                </div>

                {/* Step Content - No dotted line after this step */}
                <div className="md:ml-8 md:sm:ml-12 pb-8 md:pb-16 relative">
                  <h2 className="text-2xl font-heading font-bold mb-4 text-text-primary-light dark:text-text-primary-dark flex items-center gap-3">
                    {/* Mobile Step Number Badge */}
                    <span className={`md:hidden flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${activeStep >= 4 ? 'bg-primary-light dark:bg-primary-dark text-white' : 'bg-gray-400 dark:bg-gray-600 text-white'}`}>
                      4
                    </span>
                    Share & Earn Rewards
                  </h2>
                  <div className="bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md border border-border-light dark:border-border-dark rounded-2xl p-6  transition-all duration-300 hover:">
                    <p className="text-base text-text-secondary-light dark:text-text-secondary-dark mb-6 text-center">
                      Share your code with others and earn rewards when they sign up using your referral code:
                    </p>

                    {userData.referralCode && (
                      <div className={`bg-background-light/50 dark:bg-background-dark/50 border border-border-light dark:border-border-dark rounded-lg p-4 mb-6 text-center transition-all duration-300 ${activeStep < 4 ? 'blur-sm pointer-events-none' : ''
                        }`}>
                        <h3 className="font-medium text-lg mb-2 text-text-primary-light dark:text-text-primary-dark">Your referral link:</h3>
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <code className="text-sm block p-3 rounded-md bg-primary-light/10 dark:bg-primary-dark/10 font-mono text-primary-light dark:text-primary-dark text-lg max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                            {import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL}/signup?ref={userData.referralCode}
                          </code>
                          <button
                            className="flex-shrink-0 p-2 rounded-full bg-surface-light dark:bg-surface-dark hover:bg-surface-light/80 dark:hover:bg-surface-dark/80 transition-colors"
                            onClick={() => {
                              navigator.clipboard.writeText(`${import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL}/signup?ref=${userData.referralCode}`);
                              showMessage('Referral code copied to clipboard!');
                            }}
                            disabled={activeStep < 4}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-primary-light dark:text-text-primary-dark"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                          </button>
                        </div>
                        {activeStep < 4 && (
                          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-2">
                            Complete all previous steps to unlock your referral link
                          </p>
                        )}
                      </div>
                    )}
                    <div className="space-y-6 max-w-xl mx-auto text-left">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-light/20 dark:bg-primary-dark/20 flex items-center justify-center text-primary-light dark:text-primary-dark">
                          <Gift size={20} />
                        </div>
                        <div>
                          <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark">Cash Rewards</h4>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                            Earn {import.meta.env.VITE_AMBASSADOR_PROGRAM_REFERRAL_PERCENTAGE || 10}% commission on every subscription purchased with your code
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-light/20 dark:bg-primary-dark/20 flex items-center justify-center text-primary-light dark:text-primary-dark">
                          <Clock size={20} />
                        </div>
                        <div>
                          <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark">Free Interview Minutes</h4>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                            Earn additional interview minutes for each successful referral
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-light/20 dark:bg-primary-dark/20 flex items-center justify-center text-primary-light dark:text-primary-dark">
                          <Sparkles size={20} />
                        </div>
                        <div>
                          <h4 className="font-medium text-text-primary-light dark:text-text-primary-dark">Exclusive Access</h4>
                          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                            Get early access to new features and premium content
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 text-center">
                      <div className="p-4 bg-primary-light/10 dark:bg-primary-dark/10 rounded-lg inline-block">
                        <p className="text-primary-light dark:text-primary-dark text-base">
                          🎉 You're all set! Start sharing your referral code and earn rewards!
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row justify-center gap-3 my-2">
                      <Button 
                        variant="primary" 
                        onClick={() => window.open('https://platform.mocktrail.app/settings-preferences?utm_source=ambassador_program&utm_medium=referral_link&utm_campaign=ambassador_program', '_blank')}
                        leftIcon={<User2 size={16} />}
                        className="mt-6">
                        Manage Referrals
                      </Button>
                      <Button 
                        variant="secondary" 
                        rightIcon={<Book size={16} />}
                        onClick={() => navigate('/ambassador-guide')}
                        className="mt-2 md:mt-6">
                        Read Ambassador Guide
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* FAQ Section */}
        <div ref={faqSectionRef} className="max-w-6xl mx-auto mt-24">
          <div className={`text-center mb-12 transform transition-all duration-500 ${faqsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl font-heading font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary-light via-text-primary-light to-primary-light dark:from-primary-dark dark:via-text-primary-dark dark:to-primary-dark">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto">
              Everything you need to know about the MockTrail Ambassador Program
            </p>
          </div>

          <div className="space-y-0 divide-y divide-border-light dark:divide-border-dark">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFaqIndex === index}
                onClick={() => toggleFaq(index)}
                index={index}
                isVisible={faqsVisible}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Toast Message */}
      {message && (
        <Toast
          message={message.text}
          type={message.type}
          visible={!!message}
          onClose={() => setMessage(null)}
          duration={3000}
        />
      )}

      {/* Bank Details Modal */}
      {showBankDetailsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Close modal when clicking on backdrop
            if (e.target === e.currentTarget) {
              handleCloseBankDetailsModal();
            }
          }}
        >
          <div className="bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
                  Edit Bank Details
                </h3>
                <button
                  onClick={handleCloseBankDetailsModal}
                  className="p-2 hover:bg-surface-light dark:hover:bg-surface-dark rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-secondary-light dark:text-text-secondary-dark">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <form onSubmit={handleModalSubmitBankDetails} className="space-y-5">
                {/* Account Holder Name */}
                <div>
                  <label htmlFor="modal-accountHolderName" className="block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                    Account Holder Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="modal-accountHolderName"
                    name="accountHolderName"
                    value={modalBankDetails.accountHolderName}
                    onChange={handleModalInputChange}
                    className={`w-full text-base px-4 py-3 bg-surface-light dark:bg-surface-dark border ${modalFormErrors.accountHolderName ? 'border-red-500' : 'border-border-light dark:border-border-dark'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark text-text-primary-light dark:text-text-primary-dark`}
                    placeholder="Enter full name as per bank records"
                  />
                  {modalFormErrors.accountHolderName && (
                    <p className="mt-1 text-sm text-red-500">{modalFormErrors.accountHolderName}</p>
                  )}
                </div>

                {/* Bank Name */}
                <div>
                  <label htmlFor="modal-bankName" className="block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                    Bank Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="modal-bankName"
                    name="bankName"
                    value={modalBankDetails.bankName}
                    onChange={handleModalInputChange}
                    className={`w-full text-base px-4 py-3 bg-surface-light dark:bg-surface-dark border ${modalFormErrors.bankName ? 'border-red-500' : 'border-border-light dark:border-border-dark'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark text-text-primary-light dark:text-text-primary-dark`}
                    placeholder="e.g., HDFC Bank, SBI, ICICI Bank"
                  />
                  {modalFormErrors.bankName && (
                    <p className="mt-1 text-sm text-red-500">{modalFormErrors.bankName}</p>
                  )}
                </div>

                {/* Account Number */}
                <div>
                  <label htmlFor="modal-accountNumber" className="block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                    Account Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showAccountNumber ? "text" : "password"}
                      id="modal-accountNumber"
                      name="accountNumber"
                      value={modalBankDetails.accountNumber}
                      onChange={handleModalInputChange}
                      className={`w-full text-base px-4 py-3 pr-12 bg-surface-light dark:bg-surface-dark border ${modalFormErrors.accountNumber ? 'border-red-500' : 'border-border-light dark:border-border-dark'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark text-text-primary-light dark:text-text-primary-dark`}
                      placeholder="Enter 9-18 digit account number"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 px-4 flex items-center text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark"
                      onClick={() => setShowAccountNumber(!showAccountNumber)}
                    >
                      {showAccountNumber ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>
                  {modalFormErrors.accountNumber && (
                    <p className="mt-1 text-sm text-red-500">{modalFormErrors.accountNumber}</p>
                  )}
                  <p className="mt-1 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    Only the last 4 digits will be visible after saving
                  </p>
                </div>

                {/* IFSC Code */}
                <div>
                  <label htmlFor="modal-ifscCode" className="block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-1">
                    IFSC Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="modal-ifscCode"
                    name="ifscCode"
                    value={modalBankDetails.ifscCode}
                    onChange={handleModalInputChange}
                    className={`w-full text-base px-4 py-3 bg-surface-light dark:bg-surface-dark border ${modalFormErrors.ifscCode ? 'border-red-500' : 'border-border-light dark:border-border-dark'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark text-text-primary-light dark:text-text-primary-dark`}
                    placeholder="e.g., HDFC0001234"
                    style={{ textTransform: 'uppercase' }}
                  />
                  {modalFormErrors.ifscCode && (
                    <p className="mt-1 text-sm text-red-500">{modalFormErrors.ifscCode}</p>
                  )}
                  <p className="mt-1 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    IFSC code format: 4 letters (bank code) + 0 + 6 alphanumeric characters (branch code)
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 pt-6">
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={handleCloseBankDetailsModal}
                    disabled={modalSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    loading={modalSubmitting}
                    disabled={modalSubmitting}
                  >
                    {modalSubmitting ? 'Updating...' : 'Update Bank Details'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmbassadorProgram; 
